import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { salaryAdjustApi, F9440, DetailRequest } from '../../../services/salaryAdjustApi';

export type Mode = 'browse' | 'add' | 'edit';

export interface DetailRow {
  f9441_02: number;
  f9441_11: string;   // 薪資代號
  f9441_11a: string;  // 薪資名稱
  f9441_12: number;   // 調整後金額
  f9441_21: number;   // 原有金額
}

export function useSalaryAdjustForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F9440[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields (header)
  const [empCode, setEmpCode] = useState('');
  const [month, setMonth] = useState('');
  const [laborIns, setLaborIns] = useState(0);
  const [healthIns, setHealthIns] = useState(0);
  const [deptCode, setDeptCode] = useState('');
  // Form fields (details)
  const [details, setDetails] = useState<DetailRow[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await salaryAdjustApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setEmpCode(current.f9440_10);
      setMonth(current.f9440_20);
      setLaborIns(current.f9440_21);
      setHealthIns(current.f9440_23);
      setDeptCode(current.f9440_41);
      setDetails(
        (current.details || []).map(d => ({
          f9441_02: d.f9441_02,
          f9441_11: d.f9441_11,
          f9441_11a: d.f9441_11a,
          f9441_12: d.f9441_12,
          f9441_21: d.f9441_21,
        }))
      );
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setEmpCode('');
    setMonth(new Date().toISOString().slice(0, 7).replace('-', ''));
    setLaborIns(0);
    setHealthIns(0);
    setDeptCode('');
    setDetails([]);
  };

  // Start edit mode (BR-U05: employee code locked)
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setEmpCode(current.f9440_10);
    setMonth(current.f9440_20);
    setLaborIns(current.f9440_21);
    setHealthIns(current.f9440_23);
    setDeptCode(current.f9440_41);
    setDetails(
      (current.details || []).map(d => ({
        f9441_02: d.f9441_02,
        f9441_11: d.f9441_11,
        f9441_11a: d.f9441_11a,
        f9441_12: d.f9441_12,
        f9441_21: d.f9441_21,
      }))
    );
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setEmpCode(current.f9440_10);
      setMonth(current.f9440_20);
      setLaborIns(current.f9440_21);
      setHealthIns(current.f9440_23);
      setDeptCode(current.f9440_41);
      setDetails(
        (current.details || []).map(d => ({
          f9441_02: d.f9441_02,
          f9441_11: d.f9441_11,
          f9441_11a: d.f9441_11a,
          f9441_12: d.f9441_12,
          f9441_21: d.f9441_21,
        }))
      );
    }
  };

  // Add detail row (BR-F13)
  const addDetail = () => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.f9441_02), 0);
    setDetails([...details, { f9441_02: maxSeq + 1, f9441_11: '', f9441_11a: '', f9441_12: 0, f9441_21: 0 }]);
  };

  // Remove detail row (BR-F14)
  const removeDetail = (idx: number) => {
    setDetails(details.filter((_, i) => i !== idx));
  };

  // Update detail row
  const updateDetail = (idx: number, field: keyof DetailRow, value: string | number) => {
    setDetails(details.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));
  };

  // Save
  const save = async () => {
    try {
      const detailReqs: DetailRequest[] = details.map(d => ({
        f9441_02: d.f9441_02,
        f9441_11: d.f9441_11,
        f9441_11a: d.f9441_11a,
        f9441_12: d.f9441_12,
        f9441_21: d.f9441_21,
      }));

      let savedId: string;
      if (mode === 'add') {
        const result = await salaryAdjustApi.create({
          f9440_10: empCode,
          f9440_20: month,
          f9440_21: laborIns,
          f9440_23: healthIns,
          f9440_41: deptCode,
          details: detailReqs,
        });
        savedId = result.f9440_01;
        message.success('新增成功');
      } else {
        await salaryAdjustApi.update(current!.f9440_01, {
          f9440_20: month,
          f9440_21: laborIns,
          f9440_23: healthIns,
          f9440_41: deptCode,
          details: detailReqs,
        });
        savedId = current!.f9440_01;
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => r.f9440_01 === savedId);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await salaryAdjustApi.delete(current.f9440_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f9440_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    empCode, setEmpCode, month, setMonth,
    laborIns, setLaborIns, healthIns, setHealthIns,
    deptCode, setDeptCode,
    details, addDetail, removeDetail, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
